import { z } from 'zod';
import { GameStatus } from '../stateMachine/gameStatus.js';

/**
 * GameManifest - Unified manifest for every game in the platform.
 *
 * The manifest is the single source of truth for game metadata.
 * The Dynamic Game Loader reads the manifest to determine which
 * runtime to load and how to configure it.
 *
 * No business rules are hardcoded in the UI — all game metadata
 * comes from the manifest.
 */
/**
 * RuntimeType - The two officially supported game runtimes.
 *
 * - phaser: For reaction / action games that need a physics engine,
 *   particle effects, or complex animations.
 * - dom: For clickers, text, UI-heavy, card, and simple-interaction games.
 *   Pure DOM + CSS, no external resources.
 *
 * Other runtimes (canvas, svg, external) are NOT supported in M3.
 */
export const RuntimeType = z.enum(['phaser', 'dom']);

export type RuntimeType = z.infer<typeof RuntimeType>;

/** @deprecated Use RuntimeType instead. Kept for backward compatibility. */
export const GameRuntimeType = RuntimeType;

export type GameRuntimeType = z.infer<typeof GameRuntimeType>;

export const GameManifestSchema = z.object({
  id: z.string().uuid(),
  slug: z.string().min(2).max(100).regex(/^[a-z0-9-]+$/),
  title: z.string().min(1).max(200),
  description: z.string().max(2000).optional().default(''),
  version: z.string().regex(/^\d+\.\d+\.\d+$/),
  template_id: z.string().uuid().nullable(),
  runtime: GameRuntimeType,
  status: z.nativeEnum(GameStatus),
  difficulty: z.enum(['easy', 'medium', 'hard', 'expert']),
  duration: z.number().min(0).describe('Expected game duration in seconds'),
  features: z.array(z.string()).default([]),
  entrypoint: z.string().min(1).describe('Module path or URL to the game runtime entry'),
  analytics_schema: z.record(z.unknown()).default({}).describe('Custom event schema for this game'),
});

export type GameManifest = z.infer<typeof GameManifestSchema>;

export const GameManifestCreateInput = GameManifestSchema.omit({
  id: true,
  status: true,
}).extend({
  status: z.nativeEnum(GameStatus).default(GameStatus.DRAFT),
  description: z.string().max(2000).optional().default(''),
  features: z.array(z.string()).optional().default([]),
  analytics_schema: z.record(z.unknown()).optional().default({}),
});

export type GameManifestCreateInput = z.infer<typeof GameManifestCreateInput>;
